import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Mail, MessageSquare, MessageCircle, Star, Copy, History, Send, X } from "lucide-react";
import api from "@/lib/api";

const CHANNEL_META = {
  email: { label: "Email", icon: Mail, classes: "bg-indigo-400/10 text-indigo-300" },
  sms: { label: "SMS", icon: MessageSquare, classes: "bg-amber-400/10 text-amber-300" },
  whatsapp: { label: "WhatsApp", icon: MessageCircle, classes: "bg-emerald-400/10 text-emerald-300" },
};

const EMPTY_FORM = { id: null, name: "", channel: "email", subject: "", body_html: "", body_text: "", folder: "" };

export default function TemplatesPanel({ twilioConfigured, onTemplatesChanged }) {
  const [templates, setTemplates] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [versionsFor, setVersionsFor] = useState(null);
  const [versions, setVersions] = useState([]);
  const [testSendFor, setTestSendFor] = useState(null);
  const [testTarget, setTestTarget] = useState("");

  const loadTemplates = () => {
    api.get("/admin/crm/templates").then((r) => setTemplates(r.data)).catch(() => toast.error("Could not load templates"));
  };

  useEffect(() => { loadTemplates(); }, []);

  const editTemplate = (t) => {
    setForm({ id: t.id, name: t.name, channel: t.channel, subject: t.subject || "", body_html: t.body_html || "", body_text: t.body_text || "", folder: t.folder || "" });
    setShowForm(true);
  };

  const resetForm = () => { setForm(EMPTY_FORM); setShowForm(false); };

  const saveTemplate = async () => {
    if (!form.name.trim()) { toast.error("Template name is required"); return; }
    const body = form.channel === "email" ? form.body_html : form.body_text;
    if (!body?.trim()) { toast.error("Message body is required"); return; }
    setSaving(true);
    try {
      const payload = { name: form.name, channel: form.channel, subject: form.subject, body_html: form.body_html, body_text: form.body_text, folder: form.folder || null };
      if (form.id) {
        await api.put(`/admin/crm/templates/${form.id}`, payload);
        toast.success("Template updated (previous version saved)");
      } else {
        await api.post("/admin/crm/templates", payload);
        toast.success("Template created");
      }
      resetForm();
      loadTemplates();
      onTemplatesChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save template");
    } finally {
      setSaving(false);
    }
  };

  const deleteTemplate = async (id) => {
    await api.delete(`/admin/crm/templates/${id}`);
    setTemplates((prev) => prev.filter((t) => t.id !== id));
    toast.success("Template deleted");
    onTemplatesChanged?.();
  };

  const toggleFavorite = async (t) => {
    const { data } = await api.post(`/admin/crm/templates/${t.id}/favorite`);
    setTemplates((prev) => prev.map((x) => (x.id === t.id ? { ...x, is_favorite: data.is_favorite } : x)));
  };

  const duplicateTemplate = async (id) => {
    await api.post(`/admin/crm/templates/${id}/duplicate`);
    toast.success("Template duplicated");
    loadTemplates();
  };

  const openVersions = async (id) => {
    setVersionsFor(id);
    const { data } = await api.get(`/admin/crm/templates/${id}/versions`);
    setVersions(data);
  };

  const restoreVersion = async (versionId) => {
    await api.post(`/admin/crm/templates/${versionsFor}/restore/${versionId}`);
    toast.success("Version restored");
    setVersionsFor(null);
    loadTemplates();
  };

  const sendTest = async () => {
    if (!testTarget.trim()) { toast.error("Enter an email or phone number"); return; }
    const t = templates.find((x) => x.id === testSendFor);
    try {
      const { data } = await api.post(`/admin/crm/templates/${testSendFor}/test-send`, t.channel === "email" ? { to_email: testTarget } : { to_phone: testTarget });
      toast.success(`Test send status: ${data.status}`);
      setTestSendFor(null); setTestTarget("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test send failed");
    }
  };

  const visibleTemplates = favoritesOnly ? templates.filter((t) => t.is_favorite) : templates;
  const grouped = visibleTemplates.reduce((acc, t) => {
    const key = t.folder || "Uncategorized";
    (acc[key] = acc[key] || []).push(t);
    return acc;
  }, {});

  return (
    <div data-testid="crm-templates-panel">
      <div className="flex justify-between items-center mb-4">
        <label className="flex items-center gap-1.5 text-xs text-slate-400">
          <input type="checkbox" checked={favoritesOnly} onChange={(e) => setFavoritesOnly(e.target.checked)} data-testid="crm-templates-favorites-only-checkbox" /> Favorites only
        </label>
        <button onClick={() => (showForm ? resetForm() : setShowForm(true))} data-testid="crm-add-template-toggle-btn"
          className="flex items-center gap-1.5 text-xs font-semibold bg-primary hover:bg-primary/90 text-white px-3 py-2 rounded-lg">
          <Plus className="w-3.5 h-3.5" /> {form.id ? "Cancel Edit" : "New Template"}
        </button>
      </div>

      {showForm && (
        <div className="glass-panel rounded-xl p-4 mb-6" data-testid="crm-template-form">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Template Name"
              data-testid="crm-template-name-input" className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white" />
            <select value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} data-testid="crm-template-channel-select"
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white">
              <option value="email">Email</option>
              <option value="sms" disabled={!twilioConfigured}>SMS {!twilioConfigured && "(coming soon)"}</option>
              <option value="whatsapp" disabled={!twilioConfigured}>WhatsApp {!twilioConfigured && "(coming soon)"}</option>
            </select>
            <input value={form.folder} onChange={(e) => setForm({ ...form, folder: e.target.value })} placeholder="Folder (e.g. Onboarding)"
              data-testid="crm-template-folder-input" className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white" />
          </div>
          {form.channel === "email" ? (
            <>
              <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Subject — use {{FirstName}} for personalization"
                data-testid="crm-template-subject-input" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white mb-3" />
              <textarea value={form.body_html} onChange={(e) => setForm({ ...form, body_html: e.target.value })} rows={6}
                placeholder="Email HTML body — supports {{FirstName}}, {{Email}}, {{Company}}, {{CurrentDate}}"
                data-testid="crm-template-body-html-input" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white font-mono" />
            </>
          ) : (
            <textarea value={form.body_text} onChange={(e) => setForm({ ...form, body_text: e.target.value })} rows={4}
              placeholder="Message text — supports {{FirstName}}, {{Email}}"
              data-testid="crm-template-body-text-input" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white" />
          )}
          <button onClick={saveTemplate} disabled={saving} data-testid="crm-save-template-btn"
            className="mt-3 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 text-xs font-semibold px-4 py-2 rounded-lg disabled:opacity-50">
            {saving ? "Saving…" : form.id ? "Update Template" : "Create Template"}
          </button>
        </div>
      )}

      {Object.entries(grouped).map(([folder, items]) => (
        <div key={folder} className="mb-6" data-testid={`crm-template-folder-${folder}`}>
          <p className="text-xs font-semibold text-slate-400 uppercase mb-2">{folder}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {items.map((t) => {
              const meta = CHANNEL_META[t.channel] || CHANNEL_META.email;
              const Icon = meta.icon;
              return (
                <div key={t.id} className="glass-panel rounded-xl p-4" data-testid={`crm-template-card-${t.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-white">{t.name}</p>
                      <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full mt-1 ${meta.classes}`}>
                        <Icon className="w-3 h-3" /> {meta.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => toggleFavorite(t)} data-testid={`crm-favorite-template-btn-${t.id}`}>
                        <Star className={`w-3.5 h-3.5 ${t.is_favorite ? "text-amber-400 fill-amber-400" : "text-slate-500"}`} />
                      </button>
                      <button onClick={() => duplicateTemplate(t.id)} data-testid={`crm-duplicate-template-btn-${t.id}`} className="text-slate-400 hover:text-white"><Copy className="w-3.5 h-3.5" /></button>
                      <button onClick={() => openVersions(t.id)} data-testid={`crm-versions-template-btn-${t.id}`} className="text-slate-400 hover:text-white"><History className="w-3.5 h-3.5" /></button>
                      <button onClick={() => setTestSendFor(t.id)} data-testid={`crm-test-send-template-btn-${t.id}`} className="text-slate-400 hover:text-white"><Send className="w-3.5 h-3.5" /></button>
                      <button onClick={() => editTemplate(t)} data-testid={`crm-edit-template-btn-${t.id}`} className="text-slate-400 hover:text-white"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => deleteTemplate(t.id)} data-testid={`crm-delete-template-btn-${t.id}`} className="text-red-400 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                  {t.subject && <p className="text-xs text-slate-400 mt-2">Subject: {t.subject}</p>}
                  <p className="text-xs text-slate-500 mt-2 line-clamp-2">{t.body_html || t.body_text}</p>
                  {t.variables?.length > 0 && (
                    <div className="flex gap-1 flex-wrap mt-2">
                      {t.variables.map((v) => <span key={v} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-slate-300">{`{{${v}}}`}</span>)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
      {visibleTemplates.length === 0 && <p className="text-sm text-slate-400 text-center py-8" data-testid="crm-no-templates">No templates yet — create one to start sending campaigns.</p>}

      {versionsFor && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setVersionsFor(null)} data-testid="crm-versions-modal">
          <div className="glass-panel rounded-2xl p-5 max-w-lg w-full max-h-[70vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-white">Version History</p>
              <button onClick={() => setVersionsFor(null)}><X className="w-4 h-4 text-slate-400 hover:text-white" /></button>
            </div>
            {versions.length === 0 && <p className="text-xs text-slate-500">No previous versions yet — edit this template to start tracking history.</p>}
            {versions.slice().reverse().map((v) => (
              <div key={v.id} className="bg-white/5 rounded-lg p-3 mb-2" data-testid={`crm-version-row-${v.id}`}>
                <p className="text-xs text-slate-400 mb-1">{new Date(v.saved_at).toLocaleString()}</p>
                <p className="text-xs text-slate-300 line-clamp-2">{v.subject || v.body_text}</p>
                <button onClick={() => restoreVersion(v.id)} data-testid={`crm-restore-version-btn-${v.id}`} className="text-xs text-indigo-300 hover:text-indigo-200 mt-1">Restore this version</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {testSendFor && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setTestSendFor(null)} data-testid="crm-test-send-modal">
          <div className="glass-panel rounded-2xl p-5 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <p className="text-sm font-semibold text-white mb-3">Send a Test</p>
            <input value={testTarget} onChange={(e) => setTestTarget(e.target.value)} placeholder="Email or phone number" data-testid="crm-test-send-target-input"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white mb-3" />
            <button onClick={sendTest} data-testid="crm-confirm-test-send-btn" className="bg-primary hover:bg-primary/90 text-white text-xs font-semibold px-4 py-2 rounded-lg">Send Test</button>
          </div>
        </div>
      )}
    </div>
  );
}
